/* eslint-disable @typescript-eslint/semi */
/* eslint-disable operator-linebreak */
import { AppSyncFarm } from 'common/models/farm';
import { Nullable, Optional } from 'common/models/optional';
import { FarmUserInfo } from 'common/models/token';
import { OutboundLink } from 'gatsby-plugin-google-gtag';
import React from 'react';
import { FarmUtils } from 'utils/farmUtils';
import { VaultModalEvent } from 'xstate/vaultModalEvent';
import {
  addLiquidityModalService,
  initialVaultModalContext,
} from 'xstate/vaultModalMachine';
import { Link } from 'gatsby';
import { TokenIcon } from '../TokenIcons/TokenIcon';
import { TokenIcons } from '../TokenIcons/TokenIcons';
import AddLiquidityModalContainer from './AddLiquidityModalContainer';
import VaultAddLiquidityTab from './VaultAddLiquidityTab';
import { ChainId } from 'constants/constants';

type Props = {
  farm: Nullable<AppSyncFarm>;
  farmUserInfo: Optional<FarmUserInfo>;
  type: string;
  refetchUserInfo: () => Promise<void>;
  updatePool: (poolId: number) => Promise<void>;
  loadingUserInfo: boolean;
  vaultIRR?: number;
};

const Vault: React.FC<Props> = (props) => {
  const [isDepositModalOpen, setIsDepositModalOpen] = React.useState(
    initialVaultModalContext.isDepositModalOpen
  );
  React.useEffect(() => {
    const subscription = addLiquidityModalService.subscribe((state) => {
      if (state.context.isDepositModalOpen !== isDepositModalOpen) {
        setIsDepositModalOpen(() => state.context.isDepositModalOpen);
      }
      // if (state.context.isWithdrawModalOpen !== isWithdrawModalOpen) {
      //   setIsWithdrawModalOpen(() => state.context.isWithdrawModalOpen);
      // }
    });

    return subscription.unsubscribe;
    // Note that if isDepositModalOpen, isWithdrawModalOpen are not included in the array here
    // Then the function will not re-render
  }, [addLiquidityModalService, isDepositModalOpen]);

  const openDepositModal = () => {
    addLiquidityModalService.send(VaultModalEvent.OPEN_DEPOSIT_MODAL);
  };

  const farm = props.farm;
  const vaultType = props.type;

  // show irr 15% for new vaults with low irr
  const irr =
    (farm?.tvl ?? 0) < 10000 && (props.vaultIRR ?? 0) < 1 ? 15 : props.vaultIRR;

  const baseToken = FarmUtils.getBaseToken(farm);
  const memberToken = FarmUtils.getRewardToken(farm);

  const isHodlVault = FarmUtils.isHodlVault(props.farm);
  const goBackLink = '/';

  const isEthVault =
    baseToken?.displayName === 'wETH' && farm?.chainId !== ChainId.Polygon &&
    props.farm?.chainId !== ChainId.Celo;
  const baseTokenDisplayName = isEthVault ? 'ETH' : baseToken?.displayName;
  const memberTokenDisplayName =
    memberToken?.displayName === 'wETH' ? 'ETH' : memberToken?.displayName;

  const vaultName = `${baseTokenDisplayName}-${memberTokenDisplayName} Vault`;

  const vaultToolTip = isEthVault ? (
    <div className="section-description vault-description text-left">
      <b>Tip:</b> Deposit only $ETH (or $wETH) and receive LP tokens
      representing your share of the vault. These tokens will automatically earn
      fees in both assets listed below proportional to your share of the pool
      and can be redeemed at any time. Upon withdrawal ETH assets will be
      returned as wETH.
      <OutboundLink
        href="https://docs.ichi.org/ichi-docs-v3/ichi-vaults/overview"
        className="ml-4 selected-color font-medium"
        target="blank"
      >
        Learn about Vaults
      </OutboundLink>
      <br />
      <br />
      Depositing to a vault comes with risks:
      <OutboundLink
        href="https://docs.ichi.org/ichi-docs-v3/resources/risks#liquidity-provision-and-deposits-to-vaults"
        className="ml-4  selected-color font-medium"
        target="blank"
      >
        Learn More
      </OutboundLink>
    </div>
  ) : (
    <div className="section-description vault-description text-left">
      <b>Tip:</b> Deposit only ${baseTokenDisplayName} and receive LP tokens
      representing your share of the vault. These tokens will automatically earn
      fees in both assets listed below proportional to your share of the pool
      and can be redeemed at any time.
      <OutboundLink
        href="https://docs.ichi.org/ichi-docs-v3/ichi-vaults/overview"
        className="ml-4 selected-color font-medium"
        target="blank"
      >
        Learn about Vaults
      </OutboundLink>
      <br />
      <br />
      Depositing to a vault comes with risks:
      <OutboundLink
        href="https://docs.ichi.org/ichi-docs-v3/resources/risks#liquidity-provision-and-deposits-to-vaults"
        className="ml-4  selected-color font-medium"
        target="blank"
      >
        Learn More
      </OutboundLink>
    </div>
  );

  const totalUserLP = FarmUtils.amountInTokens(
    props.farmUserInfo?.totalLPUSD,
    baseToken
  );

  return (
    <>
      {!farm?.isHidden && (
        <div className="primary-color font-semibold text-left pt-3">
          <Link to={goBackLink} className="go-back">
            <span className="arrow-left-green"> </span> Back to Vaults
          </Link>
        </div>
      )}
      <div className="text-center pt-6 primary-text-color">
        {(!totalUserLP || totalUserLP === 0) && (
          <>
            <TokenIcon
              name={baseToken ? baseToken?.name : ''}
              width="118px"
              marginLeft="auto"
              marginRight="auto"
            />
            <div className="text-3xl font-semibold m-6">
              EARN with {baseToken?.displayName}
            </div>
            <div className="text-lg font-semibold m-6">
              ${baseToken?.displayName} vault has earned a{' '}
              <span className="text-[#1DBC7D]">{irr?.toFixed(2)}%</span>{' '}
              internal rate of return (IRR) since inception.
            </div>
            <div>
              It's easy, just complete the following steps to deposit.
              <br />
              <br />
              1. Select tokens
              <br />
              2. Approve spend
              <br />
              3. Confirm Deposit
            </div>
            <button
              className="primary-button-bg-color m-12 py-[10px] px-10 rounded-[10px]"
              type="button"
              onClick={openDepositModal}
              data-dd-action-name="Add Liquidity Modal"
            >
              <span className="primary-button-color">Get Started</span>
            </button>

            {vaultToolTip}
          </>
        )}
        {totalUserLP !== 0 && (
          <>
            <div className="vault-header">
              <div className="vault-title">{vaultName}</div>
              {isHodlVault && (
                <TokenIcons tokenNames={baseToken ? [baseToken?.name] : []} />
              )}
              {!isHodlVault && (
                <TokenIcons
                  tokenNames={memberToken ? [memberToken?.name] : []}
                />
              )}
            </div>
            {vaultToolTip}
            <div className="inner-box vault-box">
              <VaultAddLiquidityTab
                farm={farm}
                farmUserInfo={props.farmUserInfo}
                refetchUserInfo={props.refetchUserInfo}
                updatePool={props.updatePool}
                loadingUserInfo={props.loadingUserInfo}
                type={vaultType}
                onDepositOpen={openDepositModal}
              />
            </div>
          </>
        )}
      </div>
      {isDepositModalOpen && (
        <AddLiquidityModalContainer
          farm={props.farm}
          farmUserInfo={props.farmUserInfo}
          farmUserInfoLoading={props.loadingUserInfo}
          refetchFarmUserInfo={props.refetchUserInfo}
          updatePool={props.updatePool}
          type={props.type}
          isShowing={isDepositModalOpen}
          toggle={() =>
            addLiquidityModalService.send(VaultModalEvent.CLOSE_DEPOSIT_MODAL)
          }
        />
      )}
    </>
  );
};

export default Vault;
